import { queryOptions, useQuery } from '@tanstack/react-query';

import { FilterSchema } from '@/api';
import { filterEigenLinks, useLinksInHoofdstuk } from '@/api/queries/objects';
import { fetchAllLinksData } from '@/api/queries/objects/utils/fetchAllLinksData';
import { createInString } from '@/utils/createInString';

type UseBesluitHoofdstukDataProps = {
  besluitId: string;
  labelIds?: string[];
  hoofdstukId: string;
};

const getBesluitHoofdstukDataQueryOptions = ({
  besluitId,
  labelIds = [],
  hoofdstukId,
}: UseBesluitHoofdstukDataProps) => {
  return queryOptions({
    queryKey: ['besluit-hoofdstuk-data', { hoofdstukId, besluitId, labelIds }],
    queryFn: async () => {
      const VSPBFilter: FilterSchema[] = [
        { model: 'VSPB', field: 'Type', op: 'eq', value: 'Eigen' },
      ];

      if (labelIds.length > 0) {
        VSPBFilter.push({
          model: 'Label',
          field: 'ID',
          op: 'in',
          value: createInString(labelIds),
        });
      }

      const links = await useLinksInHoofdstuk.fetchQuery(
        {
          hoofdstukId,
          besluitId,
          VSPBFilter,
        },
        { staleTime: Infinity }
      );

      let filteredLinks = links ?? [];

      if (!labelIds.length) {
        // When no labels are selected, filter out all links that are not Eigen.
        filteredLinks = filterEigenLinks(links ?? []);
      } else {
        // When labels are selected, filter out paragrafen and subparagrafen without voorschriften.
        filteredLinks = filteredLinks
          .map((paragraafLink) => ({
            ...paragraafLink,
            subparagrafen: paragraafLink.subparagrafen.filter(
              (sub) => sub.voorschriften.length > 0
            ),
          }))
          .filter((paragraafLink) => paragraafLink.subparagrafen.length > 0);
      }

      return await fetchAllLinksData(filteredLinks);
    },
  });
};

export const useBesluitHoofdstukData = ({
  besluitId,
  labelIds = [],
  hoofdstukId,
}: UseBesluitHoofdstukDataProps) => {
  return useQuery(
    getBesluitHoofdstukDataQueryOptions({
      besluitId,
      labelIds,
      hoofdstukId,
    })
  );
};

useBesluitHoofdstukData.getOptions = getBesluitHoofdstukDataQueryOptions;
